/**
 * The onboarding email — sending it, and editing what it says.
 *
 * Opened for ONE user, from a row action on `/users` and from the create-user
 * flow. It shows who the message is going to, that person's role LABEL, a box
 * for the temporary password the admin has already set, and the role's stored
 * HTML body — editable.
 *
 * ── WHY A MODAL, AND WHY THIS ONE OPENS PER USER ────────────────────────────
 *
 * `/users` is index-only in this app: there is no `/users/:id` route and no
 * `getUser` in `api.ts`, so anything per-user is a modal or an inline section.
 * Same structural reason as `UserTargetsModal` beside it.
 *
 * ── THE FOUR THINGS THAT ARE NOT DECORATION ─────────────────────────────────
 *
 * 1. ⚠️ **THE PREVIEW IS A SANDBOXED `<iframe>`, NEVER `dangerouslySetInnerHTML`.**
 *    This is operator-supplied HTML with an editable box in front of it, so
 *    injecting it into the admin's own document is a self-XSS with a session
 *    token in `localStorage` a script away. `sandbox=""` is the *empty* token
 *    list — every restriction on, `allow-scripts` deliberately absent, so the
 *    frame cannot run script, navigate the top frame, submit a form or reach
 *    storage. **Do not add a token to "make the preview work"**: a template that
 *    needs script to render is a template whose behaviour nobody can inspect,
 *    and email clients do not run script either.
 *
 * 2. ⚠️ **SENDING IS IRREVERSIBLE AND OUTWARD-FACING**, so it goes behind a
 *    `ConfirmDialog` naming the person and their address, and behind a
 *    structural double-send guard: the button is `loading` (which `Button`
 *    turns into `disabled`) AND `sending` is re-checked inside the handler.
 *    `ResendApprovalControl` carries the identical pair for the identical
 *    reason — a control that appears to do nothing gets pressed again, and the
 *    recipient is mailed twice.
 *
 * 3. ⚠️ **SAVING THE TEMPLATE IS A SEPARATE, DELIBERATE ACT FROM SENDING.**
 *    An edit here defaults to *this send only*; changing what every future send
 *    for that role starts from is its own button behind its own confirmation.
 *    Pressing Send must never rewrite the template for everybody — a silent
 *    write with a `2xx` behind it that nobody would ever go looking for.
 *
 * 4. ⚠️ **A `2xx` MEANS THE RELAY ACCEPTED THE MESSAGE, NOT THAT IT ARRIVED.**
 *    Nothing on either side sees spam filtering, a rejected recipient, or a
 *    bounce minutes later. The outcome says *"check that it arrives"*. This
 *    sentence is on this repo's protected list and must not be softened to
 *    "sent successfully"; it is the same class of claim as a 200 from Xero,
 *    which this codebase has been wrong about three times.
 *
 * ── NO HELPER TEXT ──────────────────────────────────────────────────────────
 *
 * Nothing here explains a control. Every sentence on screen is an error, a
 * refusal, a reason a control is held down, or a statement of which of two
 * states the body is in — a fact the reader cannot otherwise see, and the one
 * this whole screen turns on.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { ApiError, api } from '@/lib/api';
import { roleLabel } from '@/lib/roles';
import type {
  OnboardingTemplateBlock,
  OnboardingTemplates,
  User,
  UserRole,
} from '@/lib/types';
import {
  Badge,
  Button,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  SegmentedControl,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';

export interface OnboardingEmailModalProps {
  open: boolean;
  /** The recipient. `null` is the closed state, like `UserTargetsModal`. */
  user: User | null;
  onClose: () => void;
  /**
   * Whether this reader may send and may save a template.
   *
   * The mounting page withholds the affordance entirely below the admin tier,
   * so this is normally `true` — it is here because a role can change under a
   * live access token, and because the refusal must be nameable rather than
   * arriving as a bare disabled button. The gate is the optimisation; the
   * server's 403 is the guarantee.
   */
  canManage: boolean;
  /** Why not, when `canManage` is false. Rendered as-is. */
  denial?: string | null;
}

/** How the body box is being read: as prose to edit, or as the message. */
type BodyView = 'edit' | 'preview';

const BODY_VIEWS = [
  { value: 'edit' as const, label: 'Edit' },
  { value: 'preview' as const, label: 'Preview' },
];

/** Which confirmation is on screen. One dialog, two outcomes — never two dialogs. */
type Pending = { kind: 'send' } | { kind: 'save-template' };

interface TemplatesState {
  templates: OnboardingTemplates | null;
  loading: boolean;
  /** The route answered 404 — this server does not have the feature yet. */
  unavailable: boolean;
  /** Anything else: a 403, a 500, the network. Not the same as "not built". */
  failed: string | null;
}

const TEMPLATES_IDLE: TemplatesState = {
  templates: null,
  loading: false,
  unavailable: false,
  failed: null,
};

/**
 * One role's block out of what `GET /onboarding-templates` returned.
 *
 * 🔴 **THIS FUNCTION IS WHY THE MODAL WAS BLANK, AND IT WAS NOT BROKEN.** It
 * tested `typeof found.html === 'string'` — and the server has never sent an
 * `html` field. So every lookup returned `null`, the modal rendered its honest
 * "no stored template" state, and the endpoint was answering **200** throughout.
 * The guard did exactly what it was written to do; the key it guarded was
 * invented by a client written against a server being built in parallel.
 *
 * *SUPERSEDED: the array-OR-record narrowing.* `roles` is an ARRAY — measured in
 * `onboarding-emails.service.ts`, which builds it with `userRoles.map(...)`.
 *
 * ⚠️ **THE RUNTIME CHECK STAYS ANYWAY, and it is not belt-and-braces.** It is now
 * checking the field this screen actually renders, so a future server that stops
 * sending `effectiveStepsHtml` degrades to a named state instead of putting an
 * empty textarea in front of an administrator who would then send it as the whole
 * message. **`null` here means "no block", never "an empty block".**
 *
 * Module-local on purpose: this file exports a component and nothing else, so
 * fast refresh keeps working. When a second caller appears it moves to its own
 * module rather than being exported from here.
 */
function onboardingBlockFor(
  templates: OnboardingTemplates | null,
  role: UserRole,
): OnboardingTemplateBlock | null {
  const roles = templates?.roles;
  if (!Array.isArray(roles)) return null;

  const found = (roles as readonly OnboardingTemplateBlock[]).find((block) => block?.role === role);
  return found && typeof found.effectiveStepsHtml === 'string' ? found : null;
}

/**
 * Every sentence the server gave us about a refusal, in the order they must be
 * read: what happened, then why.
 *
 * ⚠️ **BOTH, never one.** On `502 MAIL_SEND_FAILED` the top-level `message` is a
 * masked compile-time literal — *"Sending the message failed. The mail server
 * refused it."* — and the relay's own words (*"Invalid login: 535-5.7.8 …"*,
 * *"connect ETIMEDOUT"*) live in `details[0].reason`. `message ?? reason` drops
 * the only actionable half and does it in the reassuring direction, leaving a
 * tidy banner with no next step. On `422 MAIL_NOT_CONFIGURED` nothing is masked
 * and the `message` itself names the missing env keys, so it is surfaced
 * verbatim and there is usually no reason to add.
 *
 * An empty reasons list is an ORDINARY answer — a `null` reason, an error with
 * no details, or a server older than the field — so it renders nothing rather
 * than a blank line, and no sentence of ours may say the server gave no reason.
 */
function refusalLines(error: ApiError): string[] {
  const lines: string[] = [error.message];
  for (const reason of error.detailReasons) {
    if (!lines.includes(reason)) lines.push(reason);
  }
  return lines;
}

function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 12 20 4l-4.5 16-3.5-7-8-1Z" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M10.7 5.1A10.6 10.6 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-2.8 3.8" />
      <path d="M6.6 6.6A17.4 17.4 0 0 0 2 12s3.6 7 10 7a10.4 10.4 0 0 0 5.4-1.5" />
      <path d="M14.1 14.1a3 3 0 1 1-4.2-4.2" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

export function OnboardingEmailModal({
  open,
  user,
  onClose,
  canManage,
  denial,
}: OnboardingEmailModalProps) {
  const formId = useId();
  const previewId = useId();

  const [state, setState] = useState<TemplatesState>(TEMPLATES_IDLE);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  /**
   * The edited body, or `null` while the stored template is being used as-is.
   *
   * ⚠️ **`null` is not "empty" and the two must never be folded.** `null` says
   * *send what is stored*, so the request carries no `html` at all and the
   * server renders its own template; a string — including `''` — says *this
   * send uses exactly this*. A `?? ''` here would turn "unedited" into "send an
   * empty message", which the server would accept.
   */
  const [draft, setDraft] = useState<string | null>(null);
  const [view, setView] = useState<BodyView>('edit');
  const [sending, setSending] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [errorLines, setErrorLines] = useState<string[]>([]);
  const [fieldError, setFieldError] = useState<string | null>(null);
  /** The accepted-not-delivered sentence, once there is one. */
  const [outcome, setOutcome] = useState<string | null>(null);
  /** Set when the template save landed, so the two outcomes cannot be confused. */
  const [templateSaved, setTemplateSaved] = useState(false);

  /*
   * Re-entrancy guard for the send, in a ref rather than in state.
   *
   * `setSending(true)` does not take effect until the next render, so two
   * events dispatched in the same tick both see `sending === false` and both
   * fire — and this request mails a person. The button's `loading` covers the
   * ordinary double-click; the ref covers the case React's batching would let
   * through, which is exactly the pair `ResendApprovalControl` uses.
   */
  const inFlight = useRef(false);

  /*
   * Reset on open and whenever the recipient changes. A render-phase reset
   * rather than an effect: an effect paints the previous person's draft — and,
   * far worse, the previous person's typed password — for a frame first.
   */
  const resetKey = `${open ? 'open' : 'closed'}|${user?.id ?? 'none'}|${user?.role ?? ''}`;
  const [appliedKey, setAppliedKey] = useState(resetKey);
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    setPassword('');
    setShowPassword(false);
    setDraft(null);
    setView('edit');
    setSending(false);
    setSavingTemplate(false);
    setPending(null);
    setErrorLines([]);
    setFieldError(null);
    setOutcome(null);
    setTemplateSaved(false);
    /*
     * The templates go back to loading HERE rather than at the top of the
     * effect below, so the first paint after opening shows a loading body
     * instead of the previous person's — and so nothing calls setState
     * synchronously inside an effect. The effect only ever writes from its own
     * async callbacks.
     */
    setState({ ...TEMPLATES_IDLE, loading: open && user !== null });
    /*
     * ⚠️ `inFlight` is deliberately NOT reset here. A ref must not be written
     * during render, and it does not need to be: `send()` clears it in a
     * `finally`, so it is false on every path out of a request, and it dies
     * with the component on unmount.
     */
  }

  const role = user?.role ?? null;

  /*
   * The templates, read only while the modal is open. `Modal` renders nothing
   * while closed, so there is nothing to fill until then.
   *
   * ⚠️ *SUPERSEDED: "and the API's rate limit is 120/60s per IP shared across
   * the office."* It is **12,000/60s** (corrected 2026-09-17). Gating on `open`
   * is still right — it is a request nothing on screen can use — but the
   * arithmetic that reason cited is off by a factor of a hundred.
   */
  useEffect(() => {
    if (!open || !user) return;
    const controller = new AbortController();
    api
      .onboardingTemplates(controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setState({
          templates: response.data,
          loading: false,
          unavailable: false,
          failed: null,
        });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        /*
         * ⚠️ A 404 is an ORDINARY answer, not a failure. This app deploys
         * separately from the API and can be newer than it, so the route
         * genuinely may not exist yet — and the two must be worded differently,
         * because one is somebody else's deploy and the other is a fault.
         */
        const notBuilt = caught instanceof ApiError && caught.status === 404;
        setState({
          templates: null,
          loading: false,
          unavailable: notBuilt,
          failed: notBuilt
            ? null
            : caught instanceof Error
              ? caught.message
              : 'Could not read the onboarding templates.',
        });
      });
    return () => controller.abort();
  }, [open, user]);

  const stored = role ? onboardingBlockFor(state.templates, role) : null;
  /*
   * ⚠️ **THE EFFECTIVE WORDING, NEVER THE STORED OVERRIDE.** `stepsHtml` is
   * `null` until somebody saves an edit — which is the ordinary state, because
   * the templates table ships empty and the release's own wording is the
   * fallback. Reading the override here is what put an empty editor on screen.
   */
  const storedHtml = stored?.effectiveStepsHtml ?? '';
  /** What will actually be sent as the body — the edit if there is one. */
  const body = draft ?? storedHtml;
  const edited = draft !== null && draft !== storedHtml;

  /*
   * ✅ **THE PREVIEW IS OF THE WHOLE MESSAGE NOW**, because the join convention
   * is SERVED rather than guessed: `roleSlot` is the literal the shell carries
   * for a role's steps, and it arrives on the same response as the shell.
   *
   * *SUPERSEDED: previewing the block alone, on the grounds that "the placeholder
   * convention that joins a shell to a block was never published".* It is
   * published, and it always was — this client simply never asked for it.
   *
   * ⚠️ **AN ADMINISTRATOR CAN EDIT THE SLOT OUT OF THE SHELL**, in which case a
   * blind `split`/`join` composes a document with the steps SILENTLY MISSING —
   * a preview of an email that is wrong in the reassuring direction, because it
   * still looks like a finished message. So the slot is checked for, and when it
   * is absent the frame falls back to the block and says which it is showing.
   *
   * The `{{...}}` placeholders are deliberately left unsubstituted: this is the
   * TEMPLATE, and rendering a specimen name into it would invite the reader to
   * check a value the send resolves for itself.
   */
  const shellHtml = state.templates?.shell?.effectiveBodyHtml ?? null;
  const roleSlot = state.templates?.roleSlot ?? null;
  const composable =
    shellHtml !== null && roleSlot !== null && roleSlot !== '' && shellHtml.includes(roleSlot);
  const previewHtml = composable ? shellHtml.split(roleSlot).join(body) : body;

  const handleFailure = useCallback((caught: unknown) => {
    if (!(caught instanceof ApiError)) {
      setErrorLines(['Could not reach the server. Check your connection and try again.']);
      setFieldError(null);
      return;
    }

    /*
     * A per-field 422 — a malformed password, say — marks the control. Anything
     * with no field to sit on (an unrecognised key on a `.strict()` schema, the
     * two mail refusals, a 403) is a banner: keying a form-level message on a
     * field renders it nowhere while still making the map non-empty.
     */
    const routed = caught.fieldErrors.temporaryPassword ?? caught.fieldErrors.password ?? null;
    setFieldError(routed);

    if (caught.status === 404) {
      setErrorLines([
        'Sending onboarding email is not set up on the API yet — nothing was sent.',
      ]);
      return;
    }
    if (caught.status === 403) {
      setErrorLines([
        denial ??
          'Only administrators can send onboarding email. Your access may have changed since this page loaded.',
      ]);
      return;
    }
    if (routed) {
      setErrorLines([]);
      return;
    }
    /*
     * Everything else, in the server's own words — including both mail
     * refusals. `MAIL_NOT_CONFIGURED`'s message names the missing env keys and
     * `MAIL_SEND_FAILED`'s reason carries the relay's text; neither is replaced
     * with a house sentence, because a house sentence is a screen with no next
     * step.
     */
    setErrorLines([...refusalLines(caught), ...caught.formMessages]);
  }, [denial]);

  async function send() {
    if (!user) return;
    // Second half of the double-send guard. See `inFlight`.
    if (inFlight.current || sending) return;
    inFlight.current = true;
    setSending(true);
    setErrorLines([]);
    setFieldError(null);
    setOutcome(null);
    setTemplateSaved(false);
    try {
      await api.sendOnboardingEmail(user.id, {
        temporaryPassword: password,
        // Omitted entirely while unedited — an absent key says "use what is
        // stored", where sending the stored HTML back would make this client a
        // second writer of a body it does not own.
        //
        // ⚠️ `authoredHtml`, NOT `html` — corrected 2026-09-04. The old spelling
        // was a `.strict()` 422, and the name is what keeps the body out of the
        // audit trail: `f_api_logs` redacts on key names, and `auth` matches.
        ...(edited ? { authoredHtml: body } : {}),
      });
      setPending(null);
      /*
       * ⚠️ PROTECTED WORDING. A 2xx is the relay ACCEPTING the message; it is
       * not delivery. Never "sent successfully".
       */
      setOutcome(
        `Handed to the mail server for ${user.name} (${user.email}). That is acceptance, not delivery — check that it arrives.`,
      );
    } catch (caught) {
      setPending(null);
      handleFailure(caught);
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  async function saveTemplate() {
    if (!role || savingTemplate) return;
    setSavingTemplate(true);
    setErrorLines([]);
    setOutcome(null);
    setTemplateSaved(false);
    try {
      const response = await api.updateOnboardingTemplate(role, { stepsHtml: body });
      setPending(null);
      /*
       * Fold the server's own version back into the loaded templates, so the
       * draft stops reading as "edited for this send" the moment it IS the
       * stored one. Rendering from what was sent would leave the state label
       * lying about a body that now matches.
       *
       * ⚠️ **THE PATCH ANSWERS THE WHOLE VIEW, NOT THE PATCHED ROLE**, so this is
       * a REPLACEMENT rather than a splice into `roles`. That is deliberate on
       * the server's side: the `shell*` write keys reach every role from a
       * per-role path, so the only honest answer is the whole thing — and a
       * client that spliced one block back would show every OTHER role's stale
       * wording after a shell edit, with nothing failing.
       *
       * Guarded because a server older than this response shape would leave the
       * screen claiming a save it cannot show; keeping what we had is better than
       * rendering an empty view over a save that succeeded.
       */
      if (response.data && Array.isArray(response.data.roles)) {
        setState((current) => ({ ...current, templates: response.data }));
      }
      setDraft(null);
      setTemplateSaved(true);
    } catch (caught) {
      setPending(null);
      handleFailure(caught);
    } finally {
      setSavingTemplate(false);
    }
  }

  const busy = sending || savingTemplate;
  const templatesReady = !state.loading && !state.unavailable && state.failed === null;
  /**
   * Why Send is held down, or `null` when it is offerable.
   *
   * ⚠️ **Every disabled state here has a sentence.** A bare `disabled` makes a
   * permission denial, a missing template and an empty password box look
   * identical, which is the failure this repo writes up as *a disabled control
   * is a claim, and nothing typechecks it*.
   */
  const sendDenial = !canManage
    ? (denial ?? 'Your account cannot send onboarding email.')
    : state.unavailable
      ? 'Not set up on the API yet.'
      : state.failed !== null
        ? 'The templates could not be read, so there is nothing to send.'
        : state.loading
          ? 'Still reading the template.'
          : password === ''
            ? 'Type the temporary password first.'
            : body.trim() === ''
              ? 'There is no message body to send.'
              : null;

  return (
    <>
      <Modal
        open={open && user !== null}
        onClose={onClose}
        size="lg"
        dismissOnBackdrop={false}
        title={user ? `Onboarding email for ${user.name}` : 'Onboarding email'}
        footer={
          <>
            {sendDenial && (
              <p className="text-xs text-fg-muted sm:mr-auto sm:self-center">{sendDenial}</p>
            )}
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Close
            </Button>
            {/*
              Saving the template is its own act, with its own confirmation.
              It changes what EVERY future send for this role starts from, so it
              must never ride along with Send.
            */}
            <Button
              variant="secondary"
              onClick={() => setPending({ kind: 'save-template' })}
              loading={savingTemplate}
              disabled={!canManage || !edited || sending}
            >
              Save as the template
            </Button>
            <Button
              variant="primary"
              type="submit"
              form={formId}
              leadingIcon={<SendIcon />}
              loading={sending}
              disabled={sendDenial !== null || savingTemplate}
            >
              Send onboarding email
            </Button>
          </>
        }
      >
        <form
          id={formId}
          noValidate
          className="grid gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (sendDenial !== null || busy) return;
            setPending({ kind: 'send' });
          }}
        >
          {errorLines.length > 0 && (
            <Notice tone="danger" role="alert" className="break-words">
              <div className="grid gap-1">
                {errorLines.map((line) => (
                  <p key={line} className="m-0">
                    {line}
                  </p>
                ))}
              </div>
            </Notice>
          )}

          {outcome && (
            <Notice tone="warning" role="status" className="break-words">
              {outcome}
            </Notice>
          )}

          {templateSaved && (
            <Notice tone="success" role="status" className="break-words">
              Saved as the template for {role ? roleLabel(role) : 'this role'}. Every future
              onboarding email for that role starts from it. Nothing was sent.
            </Notice>
          )}

          {/* Who it is going to, and on which role's template. Not helper text:
              the role decides which body is loaded, so it is part of the
              record being acted on. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-border bg-surface-sunken px-3 py-2.5">
            <div className="min-w-0">
              <p className="m-0 truncate text-sm font-medium text-fg">{user?.name}</p>
              <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
                {user?.email ? <EmailLink address={user.email} tone="muted" /> : null}
              </p>
            </div>
            {role && (
              <Badge tone="neutral" variant="outline">
                {roleLabel(role)}
              </Badge>
            )}
          </div>

          <Field
            label="Temporary password"
            required
            {...(fieldError ? { error: fieldError } : {})}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                name="temporaryPassword"
                type={showPassword ? 'text' : 'password'}
                value={password}
                // Never `current-password`: that offers the signed-in admin's
                // own credentials for somebody else's account.
                autoComplete="new-password"
                invalid={Boolean(fieldError)}
                disabled={!canManage}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setFieldError(null);
                }}
                trailingSlot={
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    leadingIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  />
                }
              />
            )}
          </Field>

          <div className="grid gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SegmentedControl
                label="Show the message body as"
                value={view}
                options={BODY_VIEWS}
                size="sm"
                onChange={setView}
              />
              {/*
                ⚠️ WHICH STATE THE BODY IS IN, and the way back.
                An edited body that looks identical to a stored one is the whole
                risk of this screen: the admin cannot otherwise tell whether the
                next Send carries their edit or the stored template, and the two
                reach a real person. This names the state and offers the way
                back — it is not an explanation of the control.
              */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-fg-muted">
                  {state.loading
                    ? 'Reading the stored template…'
                    : state.unavailable
                      ? 'Not set up on the API yet.'
                      : state.failed !== null
                        ? 'The stored template could not be read.'
                        : edited
                          ? 'Edited — this send only.'
                          : stored === null
                            ? `No stored template for ${role ? roleLabel(role) : 'this role'}.`
                            : 'The stored template, unchanged.'}
                </span>
                {edited && (
                  <Button variant="secondary" size="sm" onClick={() => setDraft(null)}>
                    Back to the stored template
                  </Button>
                )}
              </div>
            </div>

            {state.failed !== null && (
              <Notice tone="danger" role="alert" className="break-words">
                {state.failed}
              </Notice>
            )}

            {view === 'edit' ? (
              <Field label="Message body" hideLabel>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Textarea
                    id={id}
                    aria-describedby={describedBy}
                    name="html"
                    value={body}
                    rows={14}
                    spellCheck={false}
                    disabled={!canManage || !templatesReady}
                    className="font-mono text-xs"
                    onChange={(event) => setDraft(event.target.value)}
                  />
                )}
              </Field>
            ) : (
              <div className="grid gap-1.5">
                {/*
                  ⚠️ SANDBOXED, AND `allow-scripts` IS DELIBERATELY ABSENT.
                  `sandbox=""` is the empty token list: no script, no top-frame
                  navigation, no form submission, no storage, no same-origin.
                  This is operator-supplied HTML sitting behind an editable box,
                  so putting it through `dangerouslySetInnerHTML` would run it
                  inside the admin's own session.
                */}
                <iframe
                  id={previewId}
                  title={`Preview of the onboarding email for ${user?.name ?? 'this user'}`}
                  sandbox=""
                  srcDoc={previewHtml}
                  className="h-80 w-full rounded-lg border border-border bg-white"
                />
                {/*
                  ⚠️ REMOVED 2026-09-05 under the stricter sweep, and it named a
                  state nothing else shows: when the shared shell has no slot for
                  the role's steps, the frame above is the role's BLOCK ALONE and
                  not the message that will go out. `composable` is still
                  computed and still decides how the preview is built; only the
                  sentence telling the reader they are looking at a fragment has
                  gone.
                */}
              </div>
            )}
          </div>
        </form>
      </Modal>

      {/*
        One dialog, two outcomes. Nested inside the form modal on purpose —
        `Modal` stops Escape and Tab at its own panel precisely so a dialog
        opened from another one keeps its own focus trap.
      */}
      <ConfirmDialog
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={() => {
          if (pending?.kind === 'send') void send();
          else if (pending?.kind === 'save-template') void saveTemplate();
        }}
        tone="primary"
        title={
          pending?.kind === 'save-template'
            ? `Save this as the ${role ? roleLabel(role) : 'role'} template?`
            : `Send the onboarding email to ${user?.name ?? 'this user'}?`
        }
        description={
          pending?.kind === 'save-template' ? (
            <>
              Replaces the stored onboarding template for{' '}
              <strong>{role ? roleLabel(role) : 'this role'}</strong>, so every future
              onboarding email for that role starts from it — not just this one. Nothing is
              sent.
            </>
          ) : (
            <>
              Emails <strong>{user?.name ?? 'this user'}</strong>
              {user?.email ? (
                <>
                  {' '}
                  at <strong>{user.email}</strong>
                </>
              ) : null}
              , including the temporary password you typed. It leaves SEED EMERGE and cannot
              be recalled. It does not change their password.
              {edited ? ' The edited body is used for this send only.' : ''}
            </>
          )
        }
        confirmLabel={pending?.kind === 'save-template' ? 'Save the template' : 'Send the email'}
        loading={busy}
      />
    </>
  );
}
